import { apiClient } from '@src/services/http-common'
import { isLoadLoansRunInProgress, type LoadLoansRequest, type LoadLoansRun } from '@src/types/load-loans'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

const ROUTE = 'dev-tools/load-loans'
const POLL_INTERVAL_MS = 2000

export const loadLoansQueryKeys = {
  all: ['loadLoans'] as const,
  latest: () => [...loadLoansQueryKeys.all, 'latest'] as const,
}

// null when no run exists yet.
const getLatest = async (): Promise<LoadLoansRun | null> => {
  const response = await apiClient.get<LoadLoansRun | null>(`${ROUTE}/latest`)
  return response.data
}

// DateOnly serializes to YYYY-MM-DD through toJSON, so the request posts as-is.
const postStart = async (request: LoadLoansRequest): Promise<LoadLoansRun> => {
  const response = await apiClient.post<LoadLoansRun>(ROUTE, request)
  return response.data
}

/** The latest run, polled while it is in progress. */
export function useGetLatestLoadLoansRun(): [LoadLoansRun | null | undefined, boolean] {
  const { data, isLoading } = useQuery({
    queryKey: loadLoansQueryKeys.latest(),
    queryFn: getLatest,
    refetchInterval: (query) => (isLoadLoansRunInProgress(query.state.data) ? POLL_INTERVAL_MS : false),
  })
  return [data, isLoading] as const
}

export function useStartLoadLoans() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: postStart,
    onSuccess: (run) => {
      queryClient.setQueryData(loadLoansQueryKeys.latest(), run)
    },
  })
}
